/** @format */
import {useCallback, useEffect, useId, useState} from 'react';

import {Area, ComposedChart, LabelList, Line, ResponsiveContainer, Tooltip, YAxis} from 'recharts';

import {DEFAULT_CHART_HEIGHT, MAX_PEAK_LABELS, MAX_PIT_LABELS, Y_AXIS_WIDTH} from '../../const';
import {hasNegativeValue} from '../../helpers/hasNegativeValue';
import {Chart, ChartLabel, ChartLabelList, CommonChartData} from '../../model/charts';
import {BrushStartEndIndex, ChartTypeSettings, MarginOptions} from '../../model/common';
import {TooltipDataList} from '../../model/tooltip';
import {AreaLabel} from '../area-label';
import CustomTooltip from '../tooltip';
import {CustomYAxis} from '../y-axis';
import {computeLabels} from '../../helpers/value-labels/computeLabels';

// Colour of the forecast value label — matches the dashed forecast line (gray).
const FORECAST_LABEL_COLOR = '#8b8b8b';

interface Props<ChartId extends string> {
  chart: Chart<ChartId>;
  allCharts?: Chart<ChartId>[];
  syncId?: string;
  onChartClick?: (chartId: ChartId) => void;
  tooltipData: TooltipDataList<ChartId>;
  chartIndex: number;
  chartTypeSettings: ChartTypeSettings<ChartId>;
  overrideChartHeight?: number;
  canShowYAxis?: boolean;
  margin?: MarginOptions;
  maxPeakLabels?: number;
  maxPitLabels?: number;
  openedScattersCount?: number;
  isCompactTooltip?: boolean;
  planLineDashed?: boolean;
  yAxisFromZero?: boolean;
  // Explicit value labels to render at specific points (overview "today value"). When provided it
  // replaces the computed pit/peak labels — recharts positions each at its data point.
  valueLabels?: ChartLabelList;
  // Value labels rendered on the forecast (dashed) line, e.g. the forecast value at period end.
  forecastValueLabels?: ChartLabelList;
}

const MainChart = <ChartId extends string>({
  chart,
  syncId,
  onChartClick,
  tooltipData,
  allCharts,
  chartIndex,
  chartTypeSettings,
  canShowYAxis = false,
  overrideChartHeight,
  margin,
  maxPeakLabels = MAX_PEAK_LABELS,
  maxPitLabels = MAX_PIT_LABELS,
  openedScattersCount = 0,
  isCompactTooltip,
  planLineDashed = false,
  yAxisFromZero = false,
  valueLabels,
  forecastValueLabels,
}: Props<ChartId>) => {
  const {
    chartsData: data,
    logarithmData,
    chartId,
    chartHeight,
    hide,
    color,
    skipValueLabels,
  } = chart;

  // Уникальный суффикс для id градиента заливки. id у SVG <defs> глобальны в DOM, а `fill="url(#id)"`
  // резолвится в ПЕРВЫЙ matching def. Раньше id строился только из chartId, поэтому на странице
  // overview несколько карточек с одним chartId (напр. CPL) делили один `splitColor-cpl` — и все
  // заливки брали цвет первого графика (зелёного), хотя линия у каждого своя. useId даёт уникальный
  // id на инстанс → заливка всегда совпадает со своей линией.
  const gradientUid = useId().replace(/[^a-zA-Z0-9]/g, '');

  const [labelsData, setLabelsData] = useState<ChartLabelList | undefined>();

  const chartType = chartTypeSettings[chart.chartId] || 'primary';
  const isLogarithmic = Boolean(logarithmData);

  const updateLabels = useCallback(
    (brushIndex?: Required<BrushStartEndIndex>) => {
      // Explicit labels (e.g. overview "today value") take precedence over computed pit/peak.
      const newLabels =
        valueLabels ??
        (skipValueLabels
          ? undefined
          : computeLabels({
              chartData: data,
              brushIndex,
              maxPitLabels,
              maxPeakLabels,
            }));
      setLabelsData(newLabels);
    },
    [data, maxPeakLabels, maxPitLabels, skipValueLabels, valueLabels],
  );

  useEffect(() => {
    updateLabels();

    const triggerUpdateLabels = (e: Event) => {
      const custom = e as CustomEvent<Required<BrushStartEndIndex>>;
      updateLabels(custom.detail);
    };

    window.addEventListener('multiline-chart:update-labels', triggerUpdateLabels);

    return () => {
      window.removeEventListener('multiline-chart:update-labels', triggerUpdateLabels);
    };
  }, [updateLabels]);

  if (hide) {
    return null;
  }

  // Предотвращение ситуации, если allCharts пришел undefined
  // Обозначаем текущий график как "все графики"
  const myAllCharts = allCharts ? allCharts : [chart];
  const isSingle = myAllCharts.length === 1;
  const height = overrideChartHeight ?? chartHeight ?? DEFAULT_CHART_HEIGHT;
  const secondarySuffix = chartType === 'secondary' ? 'Secondary' : '';
  const forecastDataKeyLine = `average${secondarySuffix}`;
  const forecastDataKeyArea = `range${secondarySuffix}`;
  const lineDataKey = `line${secondarySuffix}`;
  const splitColorId = `${String(chartId).replace(/[^a-zA-Z0-9]/g, '_')}-${gradientUid}`;

  return (
    <ResponsiveContainer height={height}>
      <ComposedChart
        onClick={() => onChartClick && onChartClick(chartId)}
        data={logarithmData || data}
        syncId={syncId}
        margin={margin}
      >
        {/*Градиент основного графика*/}

        <defs>
          <linearGradient id={`splitColor-${splitColorId}`} x1='0' y1='0' x2='0' y2='1'>
            <stop offset={0} stopColor={color} stopOpacity={0.4} />
            <stop offset={0.5} stopColor={color} stopOpacity={0.3} />
            <stop offset={1} stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>

        {/*График с прогнозируемыми данными*/}

        <Area
          type='monotone'
          dataKey={forecastDataKeyArea}
          fill='#555555'
          fillOpacity={0.5}
          strokeWidth={0}
          activeDot={false}
          animationDuration={0}
        />
        <Line
          type='monotone'
          dataKey={forecastDataKeyLine}
          stroke='#8b8b8b'
          strokeWidth={2}
          strokeDasharray='5 5'
          dot={false}
          animationDuration={0}
        >
          {/*Подпись к значению прогноза (напр. прогноз на конец периода)*/}
          {forecastValueLabels ? (
            <LabelList
              position='top'
              valueAccessor={(value: {payload: CommonChartData}) => {
                const name = value.payload.name;
                if (!name) return undefined;
                // cast: only some points carry a forecast label (others must yield undefined).
                const entry = forecastValueLabels[name] as ChartLabel | undefined;
                return entry ? entry[chartType] : undefined;
              }}
              content={props => <AreaLabel {...props} color={FORECAST_LABEL_COLOR} />}
            />
          ) : null}
        </Line>

        {/*Основной график*/}

        <Area
          key={chartType}
          type='monotone'
          dataKey={chartType}
          stroke={color}
          strokeWidth={2}
          fillOpacity={1}
          fill={`url(#splitColor-${splitColorId})`}
          animationDuration={0}
        >
          {/*Подпись к значениям графика*/}
          {labelsData ? (
            <LabelList
              position='top'
              valueAccessor={(value: {payload: CommonChartData}) => {
                const name = value.payload.name;
                if (!name) return undefined;
                const entry = labelsData[name];
                return entry ? entry[chartType] : undefined;
              }}
              content={props => <AreaLabel {...props} color={color} />}
            />
          ) : null}
        </Area>

        {/*Дополнительный график (белая линия)*/}

        <Line
          dataKey={lineDataKey}
          stroke='#f1f1f1'
          strokeWidth={1}
          strokeDasharray={planLineDashed ? '5 5' : undefined}
          dot={false}
          animationDuration={0}
        />

        {/*График слева со значениями, появляется только в сингловом режиме*/}

        {canShowYAxis && (
          <YAxis
            tick={props => <CustomYAxis isLogarithmic={isLogarithmic} {...props} />}
            width={Y_AXIS_WIDTH}
            interval='preserveStartEnd'
            // Важно !!! Если есть негативное значение, а tickCount = 2, то произойдёт вылет с ошибкой maximum call stack
            tickCount={hasNegativeValue(logarithmData || data) ? 3 : 2}
            domain={yAxisFromZero ? [0, 'auto'] : undefined}
            hide={!isSingle}
          />
        )}

        <Tooltip
          wrapperStyle={{zIndex: 1000}}
          content={
            <CustomTooltip
              allCharts={myAllCharts}
              allData={tooltipData}
              chartIndex={chartIndex}
              chartTypeSettings={chartTypeSettings}
              openedScattersCount={openedScattersCount}
              isCompact={isCompactTooltip}
            />
          }
          animationDuration={0}
          filterNull={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default MainChart;
